import type { RegistrationProfileAddress, SexAssignedAtBirth } from '@galapagos/shared';
import type { SignupChannel } from '@/services/session';
import type { SignedInWith } from '../profile/profile-answers';
import type { SignupSignedInContact } from '../services/signup-api/signup-api';
import type { SignupLegalName, SignupSignInContact } from '../stores/signup-flow-store';
import { signupStepsFor, type SignupPath, type SignupStepId } from '../steps';

/** The answers later steps depend on, as the flow store holds them. */
export interface SignupEarlierAnswers {
  programSelection: readonly string[] | null;
  legalName: SignupLegalName | null;
  /** Only its presence matters; an SSO sign-in has a channel and no destination. */
  signInContact: SignedInWith | null;
  birthDate: string | null;
  sexAssignedAtBirth: SexAssignedAtBirth | null;
  profileAddress: RegistrationProfileAddress | null;
}

/**
 * Whether `step` still needs the plan details: it comes after the plan details
 * step on this path and no later than the contact step, whose profile save
 * sends them. They are never stored in the browser, so a reload loses them;
 * past that save they can no longer be sent, so nothing asks again. The
 * substance step never asks: it is also where a member re-picks programs
 * after the save, and the address and contact steps after it still ask.
 */
export function needsPlanDetails(step: SignupStepId, path: SignupPath): boolean {
  const steps = signupStepsFor(path);
  const at = steps.indexOf(step);
  const asked = steps.indexOf('plan-details');
  return step !== 'substance' && asked >= 0 && at > asked && at <= steps.indexOf('contact');
}

/** Each step that collects an answer later steps need, and whether that answer is there. */
export const SIGNUP_ANSWER_COLLECTORS: ReadonlyMap<
  SignupStepId,
  (answers: SignupEarlierAnswers) => boolean
> = new Map([
  [
    'substance',
    (answers) => answers.programSelection !== null && answers.programSelection.length > 0,
  ],
  ['legal-name', (answers) => answers.legalName !== null],
  ['birth-date', (answers) => answers.birthDate !== null && answers.sexAssignedAtBirth !== null],
  ['account', (answers) => answers.signInContact !== null],
  ['address', (answers) => answers.profileAddress !== null],
]);

/**
 * Where a step must send the member instead of showing itself: the first step
 * on this path, before `step`, whose answer is missing; null when every
 * earlier answer is there. A step the path does not visit, the path's first
 * step, and the download page (which login and direct-download links reach
 * with no answers) never redirect.
 */
export function earliestMissingAnswerStep(
  step: SignupStepId,
  path: SignupPath,
  answers: SignupEarlierAnswers,
): SignupStepId | null {
  if (step === 'download') return null;
  const steps = signupStepsFor(path);
  const at = steps.indexOf(step);
  if (at <= 0) return null;
  for (const earlier of steps.slice(0, at)) {
    const collected = SIGNUP_ANSWER_COLLECTORS.get(earlier);
    if (collected !== undefined && !collected(answers)) return earlier;
  }
  return null;
}

function present(value: string | null): value is string {
  return value !== null && value.trim() !== '';
}

/**
 * The sign-in contact rebuilt from the member's record, or null when it
 * cannot be told: the channel is the session's record of how the member
 * signed in, or failing that the only contact on file; the destination is
 * that contact.
 */
export function rebuildSignInContact(
  contact: SignupSignedInContact,
  channelRecord: SignupChannel | null,
): SignupSignInContact | null {
  let channel = channelRecord;
  if (channel === null) {
    if (present(contact.email) && !present(contact.phone)) channel = 'email';
    else if (present(contact.phone) && !present(contact.email)) channel = 'phone';
  }
  if (channel === 'email' && present(contact.email)) {
    return { channel: 'email', destination: contact.email };
  }
  if (channel === 'phone' && present(contact.phone)) {
    return { channel: 'sms', destination: contact.phone };
  }
  return null;
}
